/**
 * Verify Business Sign-Up Page
 * Handles email verification token from business sign-up email link
 */

import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, XCircle, Loader, AlertCircle } from 'lucide-react';
import { verifyBusinessSignUpToken } from '../services/WebCloudFunctionsService';
import { useTheme } from '../contexts/ThemeContext';
import fitzaaLogo from '../assets/appIcon.png';

const VerifyBusinessSignUpPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { theme } = useTheme();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('');

  useEffect(() => {
    const handleVerification = async () => {
      try {
        // Get the token from the URL
        const token = searchParams.get('token');

        if (!token) {
          setStatus('error');
          setMessage('Invalid verification link. Please request a new verification email.');
          return;
        }

        // Verify the token
        const result = await verifyBusinessSignUpToken(token);

        if (result.success) {
          setStatus('success');
          setMessage('Email verified successfully! Redirecting to password setup...');

          // Redirect to password setup page with token
          setTimeout(() => {
            navigate(`/set-password?token=${token}`, { replace: true });
          }, 2000);
        } else {
          setStatus('error');
          setMessage('Verification failed. Please request a new verification email.');
        }
      } catch (error: any) {
        console.error('Business sign-up verification error:', error);
        setStatus('error');

        if (error?.code === 'deadline-exceeded' || error?.message?.includes('expired')) {
          setMessage('This verification link has expired. Please request a new verification email.');
        } else if (error?.code === 'not-found' || error?.message?.includes('Invalid')) {
          setMessage('This verification link is invalid or has already been used.');
        } else {
          setMessage(error?.message || 'An error occurred during verification. Please try again.');
        }
      }
    };

    handleVerification();
  }, [searchParams, navigate]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-blue-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white dark:bg-slate-800 rounded-2xl shadow-2xl p-8">
        <div className="text-center mb-6">
          <div className="flex items-center justify-center mb-4">
            <img src={fitzaaLogo} alt="Fitzaa" className="w-12 h-12" />
          </div>
        </div>

        <div className="text-center">
          {status === 'loading' && (
            <>
              <div className="flex items-center justify-center mb-4">
                <Loader className="w-12 h-12 text-purple-600 dark:text-purple-400 animate-spin" />
              </div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                Verifying Your Email
              </h2>
              <p className="text-gray-600 dark:text-gray-400">
                Please wait while we verify your email address...
              </p>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="flex items-center justify-center mb-4">
                <div className="p-3 bg-green-100 dark:bg-green-900/50 rounded-full">
                  <CheckCircle className="w-12 h-12 text-green-600 dark:text-green-400" />
                </div>
              </div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                Email Verified!
              </h2>
              <p className="text-gray-600 dark:text-gray-400">{message}</p>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="flex items-center justify-center mb-4">
                <div className="p-3 bg-red-100 dark:bg-red-900/50 rounded-full">
                  <XCircle className="w-12 h-12 text-red-600 dark:text-red-400" />
                </div>
              </div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                Verification Failed
              </h2>
              <p className="text-gray-600 dark:text-gray-400 mb-4">{message}</p>
              <button
                onClick={() => navigate('/admin/login', { replace: true })}
                className="px-6 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium transition-colors"
              >
                Back to Login
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default VerifyBusinessSignUpPage;

